// src/Pages/finanzas-clientes/FinanzasTab.tsx
import React from "react";
import { Card, Col, Row, Spin, Typography } from "antd";
import { FinanzasLineItem } from "./useFinanzasTab";

const { Text } = Typography;

const money = (v: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(v || 0);

type Props = {
  ingresos: FinanzasLineItem[];
  gastos: FinanzasLineItem[];
  subtotalIngresos: number;
  subtotalGastos: number;
  total: number;
  loading: boolean;
  // Egresos del cliente que NO viven en esta pestaña (arriendos + flujos) pero
  // SÍ afectan su saldo: aseos que él paga (tipo=1), productos entregados a su
  // propiedad y lavandería (tipo=1). Se pasan desde el padre para que el Total
  // de esta pestaña sea el SALDO COMPLETO, idéntico al "Total general" del
  // Resumen al pie. Antes el Total omitía estos egresos y sobreestimaba el
  // saldo del cliente. (Audit completitud 2026-06-21.)
  egresoAseosCliente?: number;
  egresoProductos?: number;
  egresoLavadosCliente?: number;
};

const LineItemList: React.FC<{
  items: FinanzasLineItem[];
  emptyText: string;
}> = ({ items, emptyText }) => {
  if (items.length === 0) {
    return (
      <Text type="secondary" style={{ display: "block", padding: "12px 0" }}>
        {emptyText}
      </Text>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "6px 0",
            borderBottom: "1px solid #f5f5f5",
          }}
        >
          <Text ellipsis style={{ maxWidth: "65%" }}>
            {item.descripcion}
          </Text>
          <Text strong>{money(item.monto)}</Text>
        </div>
      ))}
    </div>
  );
};

const FinanzasTab: React.FC<Props> = ({
  ingresos,
  gastos,
  subtotalIngresos,
  subtotalGastos,
  total,
  loading,
  egresoAseosCliente = 0,
  egresoProductos = 0,
  egresoLavadosCliente = 0,
}) => {
  // Egresos extra del cliente (no viven en arriendos/flujos) que SÍ afectan su
  // saldo. Solo se listan los > 0. El Total de abajo es el saldo COMPLETO.
  const otrosEgresos = [
    { label: "Aseos que pagas", value: egresoAseosCliente },
    { label: "Productos", value: egresoProductos },
    { label: "Lavandería", value: egresoLavadosCliente },
  ].filter((r) => r.value > 0);
  const totalCompleto =
    total - egresoAseosCliente - egresoProductos - egresoLavadosCliente;

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card bodyStyle={{ paddingTop: 12 }}>
      <Row gutter={24}>
        {/* Ingresos */}
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Text strong style={{ color: "#389e0d" }}>
                Ingresos
              </Text>
            }
            style={{ marginBottom: 16 }}
          >
            <LineItemList
              items={ingresos}
              emptyText="Sin ingresos en este período"
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 0 4px",
                borderTop: "2px solid #e8e8e8",
                marginTop: 8,
              }}
            >
              <Text strong>Subtotal</Text>
              <Text strong style={{ color: "#389e0d", fontSize: 15 }}>
                {money(subtotalIngresos)}
              </Text>
            </div>
          </Card>
        </Col>

        {/* Gastos */}
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Text strong style={{ color: "#cf1322" }}>
                Gastos
              </Text>
            }
            style={{ marginBottom: 16 }}
          >
            <LineItemList
              items={gastos}
              emptyText="Sin gastos en este período"
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 0 4px",
                borderTop: "2px solid #e8e8e8",
                marginTop: 8,
              }}
            >
              <Text strong>Subtotal</Text>
              <Text strong style={{ color: "#cf1322", fontSize: 15 }}>
                {money(subtotalGastos)}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Otros conceptos que afectan tu saldo y NO viven en arriendos/flujos:
          aseos que pagas, productos, lavandería. Se listan para que el Total
          (saldo completo) sea auto-consistente con lo mostrado. */}
      {otrosEgresos.length > 0 && (
        <Card
          size="small"
          title={<Text strong>Otros que afectan tu saldo</Text>}
          style={{ marginBottom: 16 }}
        >
          {otrosEgresos.map((r) => (
            <div
              key={r.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "6px 0",
                borderBottom: "1px solid #f5f5f5",
              }}
            >
              <Text>{r.label}</Text>
              <Text strong style={{ color: "#cf1322" }}>
                −{money(r.value)}
              </Text>
            </div>
          ))}
        </Card>
      )}

      {/* Total = saldo COMPLETO del cliente (arriendos + flujos − aseos que
          paga − productos − lavandería), idéntico al "Total general" del Resumen
          al pie. (Audit completitud 2026-06-21.) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px",
          background: "#fafafa",
          borderRadius: 8,
          marginTop: 8,
        }}
      >
        <Text style={{ fontSize: 16, fontWeight: 700 }}>Total</Text>
        <Text
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: totalCompleto >= 0 ? "#389e0d" : "#cf1322",
          }}
        >
          {money(totalCompleto)}
        </Text>
      </div>
    </Card>
  );
};

export default FinanzasTab;
